import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';

interface RouteRoleConfig {
  roles?: string[];
  requireAll?: boolean; // true = needs all roles, false = needs any role
  redirectTo?: string; // custom redirect path
}

export const authGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isLoggedIn()) {
    auth.login(state.url);
    return false;
  }

  if (auth.isSuperAdmin()) {
    return true;
  }

  const roleConfig: RouteRoleConfig = {
    roles: route.data?.['roles'] as string[],
    requireAll: (route.data?.['requireAll'] as boolean) || false,
    redirectTo: (route.data?.['redirectTo'] as string) || '/access-denied',
  };

  if (!roleConfig.roles || roleConfig.roles.length === 0) {
    return true;
  }

  const userRoles = auth.getRolesByToken();

  const hasAccess = roleConfig.requireAll
    ? hasAllRoles(userRoles, roleConfig.roles, auth)
    : hasAnyRole(userRoles, roleConfig.roles, auth);

  return hasAccess ? true : router.createUrlTree([roleConfig.redirectTo]);
};

const matchesRequiredRole = (
  requiredRole: string,
  userRoles: string[],
  auth: AuthService,
): boolean => {
  if (requiredRole === 'ADM') {
    return auth.isAdmin();
  }
  return userRoles.includes(requiredRole);
};

export const hasAnyRole = (
  userRoles: string[],
  requiredRoles: string[],
  auth: AuthService,
): boolean => requiredRoles.some((role) => matchesRequiredRole(role, userRoles, auth));

export const hasAllRoles = (
  userRoles: string[],
  requiredRoles: string[],
  auth: AuthService,
): boolean => requiredRoles.every((role) => matchesRequiredRole(role, userRoles, auth));
